<template>
  <div class="privacy-media-embed">
    <iframe
      v-if="allowed && privacyEnhancedSrc"
      ref="frame"
      class="privacy-media-embed__frame"
      :src="privacyEnhancedSrc"
      :title="title"
      :allow="allow"
      frameborder="0"
      allowfullscreen
      loading="lazy"
      referrerpolicy="strict-origin-when-cross-origin"
      @load="$emit('loaded', $event.target)"
    />
    <div v-else class="privacy-media-embed__placeholder" role="region" :aria-label="`${title} privacy controls`">
      <div class="privacy-media-embed__provider">{{ mediaProvider }}</div>
      <button
        type="button"
        class="privacy-media-embed__button"
        :aria-label="`Play ${title} and allow third-party content`"
        @click="allowContent"
      >
        <span class="privacy-media-embed__play-icon" aria-hidden="true"><span /></span>
        <span>Play video</span>
      </button>
      <div class="privacy-media-embed__details">
        <div>
          <p class="privacy-media-embed__title">{{ title }}</p>
          <p class="privacy-media-embed__copy">
            Playing allows {{ mediaProvider }} and other embedded content providers across SLMath to connect.
            They may receive information about your visit.
          </p>
        </div>
        <button type="button" class="privacy-media-embed__link" @click="$root.$emit('openCookieSettings')">
          Cookie settings
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
	COOKIE_CONSENT_EVENT,
	getCookiePreferences,
	saveCookieConsent,
} from "@/utils/cookieConsent"

export default {
	name: "PrivacyMediaEmbed",
	props: {
		src: {
			type: String,
			default: "",
		},
		title: {
			type: String,
			default: "Embedded media",
		},
		allow: {
			type: String,
			default: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
		},
	},
	data() {
		return {
			allowed: getCookiePreferences().embeddedMedia,
			playRequested: false,
		}
	},
	computed: {
		mediaProvider() {
			try {
				const hostname = new URL(this.src, window.location.origin).hostname
				if (hostname.includes("youtube")) return "YouTube"
				if (hostname.includes("vimeo")) return "Vimeo"
			} catch (_error) {
				// Fall through to a neutral label for malformed or relative provider URLs.
			}
			return "the video provider"
		},
		privacyEnhancedSrc() {
			if (!this.src) return ""
			try {
				const url = new URL(this.src, window.location.origin)
				if (["youtube.com", "www.youtube.com"].includes(url.hostname)) {
					url.hostname = "www.youtube-nocookie.com"
				}
				if (url.hostname === "player.vimeo.com") url.searchParams.set("dnt", "1")
				if (this.playRequested) url.searchParams.set("autoplay", "1")
				return url.href
			} catch (_error) {
				return ""
			}
		},
	},
	mounted() {
		window.addEventListener(COOKIE_CONSENT_EVENT, this.handleConsentChange)
	},
	beforeDestroy() {
		window.removeEventListener(COOKIE_CONSENT_EVENT, this.handleConsentChange)
	},
	methods: {
		handleConsentChange(event) {
			const allowed = event.detail.preferences.embeddedMedia
			if (this.allowed && !allowed) this.$emit("blocked")
			this.allowed = allowed
		},
		allowContent() {
			this.playRequested = true
			const preferences = getCookiePreferences()
			saveCookieConsent({ ...preferences, embeddedMedia: true })
			this.$nextTick(() => this.$refs.frame?.focus())
		},
	},
}
</script>

<style scoped lang="scss">
.privacy-media-embed {
	width: 100%;
	height: 100%;
	min-height: 12rem;
}

.privacy-media-embed__frame {
	display: block;
	width: 100%;
	height: 100%;
	min-height: inherit;
	border: 0;
}

.privacy-media-embed__placeholder {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	width: 100%;
	height: 100%;
	min-height: inherit;
	padding: 1.5rem 1.5rem 1.25rem;
	overflow: hidden;
	color: #fff;
	text-align: center;
	background:
		radial-gradient(circle at 50% 35%, rgba(139, 0, 188, 0.38), transparent 34%),
		linear-gradient(145deg, #302438 0%, #17131a 70%);
	border: 1px solid #4f3d58;
}

.privacy-media-embed__placeholder::before {
	position: absolute;
	inset: 0;
	pointer-events: none;
	content: "";
	background-image: linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px);
	background-size: 100% 3rem;
}

.privacy-media-embed__provider {
	position: absolute;
	top: 1rem;
	left: 1rem;
	z-index: 1;
	padding: 0.3rem 0.55rem;
	color: #f3e9f7;
	font-family: "Repro Bold", Arial, sans-serif;
	font-size: 0.7rem;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	background: rgba(15, 11, 17, 0.72);
	border: 1px solid rgba(255, 255, 255, 0.22);
	border-radius: 2rem;
}

.privacy-media-embed__button {
	position: relative;
	z-index: 1;
	display: flex;
	gap: 0.7rem;
	align-items: center;
	justify-content: center;
	min-height: 3.5rem;
	padding: 0.55rem 1.15rem 0.55rem 0.6rem;
	color: #fff;
	font-family: "Repro Bold", Arial, sans-serif;
	font-size: 1rem;
	background: #782d99;
	border: 2px solid #fff;
	border-radius: 2rem;
	box-shadow: 0 0.6rem 1.8rem rgba(0, 0, 0, 0.34);
	cursor: pointer;
	transition: background-color 140ms ease, transform 140ms ease, box-shadow 140ms ease;
}

.privacy-media-embed__button:hover {
	background: #8b38ad;
	box-shadow: 0 0.8rem 2.2rem rgba(0, 0, 0, 0.42);
	transform: translateY(-1px);
}

.privacy-media-embed__play-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2.25rem;
	height: 2.25rem;
	background: #fff;
	border-radius: 50%;
}

.privacy-media-embed__play-icon span {
	width: 0;
	height: 0;
	margin-left: 0.2rem;
	border-top: 0.38rem solid transparent;
	border-bottom: 0.38rem solid transparent;
	border-left: 0.58rem solid #782d99;
}

.privacy-media-embed__details {
	position: absolute;
	z-index: 1;
	right: 1rem;
	bottom: 0.85rem;
	left: 1rem;
	display: flex;
	gap: 1rem;
	align-items: flex-end;
	justify-content: space-between;
	text-align: left;
}

.privacy-media-embed__title {
	margin: 0 0 0.2rem;
	color: #fff;
	font-family: "Repro Bold", Arial, sans-serif;
}

.privacy-media-embed__copy {
	max-width: 38rem;
	margin: 0;
	color: #d9d1dc;
	font-size: 0.72rem;
	line-height: 1.35;
}

.privacy-media-embed__link {
	flex: 0 0 auto;
	padding: 0;
	color: #fff;
	font: inherit;
	font-size: 0.75rem;
	text-decoration: underline;
	background: transparent;
	border: 0;
	cursor: pointer;
}

.privacy-media-embed__button:focus-visible,
.privacy-media-embed__link:focus-visible {
	outline: 3px solid #f5b335;
	outline-offset: 3px;
}

@media (max-width: 480px) {
	.privacy-media-embed__placeholder {
		justify-content: flex-start;
		padding-top: 3.75rem;
	}

	.privacy-media-embed__details {
		align-items: flex-start;
		flex-direction: column;
		gap: 0.35rem;
	}

	.privacy-media-embed__copy {
		font-size: 0.68rem;
	}
}

@media (prefers-reduced-motion: reduce) {
	.privacy-media-embed__button {
		transition: none;
	}
}
</style>
